import { useEffect, useRef, useState } from "react";
import { ClockIcon, MapPinIcon } from "lucide-react";
import { FaInfinity } from "react-icons/fa";
import { FiShare } from "react-icons/fi";
import { RiPinDistanceLine } from "react-icons/ri";
import { toast } from "sonner";
import { convertUTCToLocalTime } from "../../../Common/ReusableFunction";

const ActivityCard = ({
  activity,
  handleViewDetails,
  manageActivity,
  buttonLabel = "View Activity",
}) => {
  const locationRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [distance, setDistance] = useState(null);
  const [distanceType, setDistanceType] = useState("km");
  const userLocation = localStorage.getItem("userLocation")
    ? JSON.parse(localStorage.getItem("userLocation"))
    : {};

  useEffect(() => {
    const el = locationRef.current;
    if (el) {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      const lines = el.scrollHeight / lineHeight;
      setIsOverflowing(lines > 1);
    }
  }, [activity.location]);

  useEffect(() => {
    const vendorLat = parseFloat(activity.latitude);
    const vendorLng = parseFloat(activity.longitude);
    const userLat = parseFloat(userLocation.latitude);
    const userLng = parseFloat(userLocation.longitude);

    if (!isNaN(vendorLat) && !isNaN(vendorLng)) {
      const d = calculateDistance(userLat, userLng, vendorLat, vendorLng);
      if (userLocation?.country !== "India") {
        const miles = d * 0.621371;
        setDistance(miles?.toFixed(2));
        setDistanceType("miles");
      } else {
        setDistance(d);
        setDistanceType("km");
      }
    }
  }, [
    activity.latitude,
    activity.longitude,
    userLocation.latitude,
    userLocation.longitude,
    userLocation?.country,
  ]);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth's radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return (R * c).toFixed(2);
  };

  const handleShareClick = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/ActivitiesDetails/${activity.activity_id}`;

    if (navigator.share) {
      navigator
        .share({
          title: activity.activity_title || "Activity",
          text: activity.description || "Check out this activity!",
          url: url,
        })
        .catch((error) => console.log("Error sharing", error));
    } else {
      navigator.clipboard
        .writeText(url)
        .then(() => toast.info("🔗 Link copied to clipboard!"))
        .catch(() => toast.error("❌ Failed to copy link."));
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-md transition-transform duration-200 hover:scale-[1.01] flex flex-col">
      {/* Image: Increased base height for better mobile visibility */}
      <div className="relative h-32 sm:h-40 md:h-40 overflow-hidden">
        <img
          src={
            activity?.original_images?.[0] ||
            activity?.uploaded_images?.[0] ||
            "https://placehold.co/400x200/cccccc/ffffff?text=No+Image"
          }
          alt={activity.activity_title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src =
              "https://placehold.co/400x200/cccccc/ffffff?text=No+Image";
          }}
        />
      </div>

      {/* Content: Adjusted padding for mobile */}
      <div className="p-3 sm:p-4 flex flex-col justify-between flex-grow">
        {/* Title: Ensured consistent line height and margin */}
        <h3 className="font-semibold text-sm leading-tight mb-2 line-clamp-2 min-h-[40px]">
          {activity.activity_title}
        </h3>

        {/* Details Section: Grouped for consistent spacing */}
        <div className="space-y-2.5 text-xs text-gray-600">
          {/* Posted By */}
          <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-600">
            <span className="font-medium whitespace-nowrap">Posted by:</span>
            <span className="truncate overflow-hidden text-ellipsis max-w-[120px] sm:max-w-[160px]">
              {activity.created_by || "Unknown"}
            </span>
          </div>

          {/* Location */}
          <div className="relative group flex items-start gap-1.5">
            <MapPinIcon className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" />
            <span ref={locationRef} className="line-clamp-2 min-w-0">
              {activity.location || "Location not specified"}
            </span>
            {isOverflowing && (
              <div className="absolute top-6 left-0 z-20 w-max max-w-[150px] md:max-w-none md:w-[250px] bg-white border border-gray-200 shadow-lg rounded-md p-2 text-sm opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-all duration-200">
                <p className="text-gray-700 whitespace-pre-line">
                  {activity.location}
                </p>
              </div>
            )}
          </div>

          <p className="flex items-center gap-1 text-xs sm:text-sm text-gray-600 mb-1">
            <RiPinDistanceLine className="w-4 h-4 text-gray-500" />
            {!isNaN(distance) ? (
              <span>
                You are{" "}
                <span className="text-black font-medium text-sm">
                  {distance || 0} {distanceType}
                </span>{" "}
                away
              </span>
            ) : (
              <span className="md:text-sm text-xs text-red-700">
                location not detected
              </span>
            )}
          </p>

          {/* Expiry */}
          <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-600 mt-1">
            <ClockIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span>Expiry:</span>
            <span className="text-orange-500 font-medium">
              {activity.infinite_time ? (
                <FaInfinity className="w-4 h-4 inline" />
              ) : activity.end_date ? (
                convertUTCToLocalTime(activity.end_date, activity.end_time)
              ) : (
                "N/A"
              )}
            </span>
          </div>
        </div>

        {/* Buttons: Added more top margin for separation */}
        <div className="mt-3 sm:mt-4 flex justify-between items-center pt-2 border-t border-gray-100">
          <button
            onClick={() => handleViewDetails(activity.activity_id)}
            className="py-2 px-4 bg-orange-500 text-white rounded-md font-medium text-xs hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap"
          >
            {manageActivity ? "Manage Activity" : buttonLabel}
          </button>
          <button
            className="p-1.5 text-gray-600 hover:text-gray-900"
            onClick={handleShareClick}
          >
            <FiShare className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityCard;
